import React from 'react';

import {
  BUILDINGS,
  BUILDING_CATEGORIES,
  Building,
  BuildingCategory,
  BuildMenuState,
  BuildingItemRequirement,
  CONTROLS,
  ItemType,
  ITEMS,
} from './constants';
import { useMapState } from './contexts/GameStateManager';
import { getGameEngine } from './GameEngine';
import { getAvailableItemCountsForPlacement } from './GameState';
import { calculateHandCraftingPostQueueInventory } from './HandCrafting';
import { createSelectBuildCategoryCommand } from './InputCommands';
import { InputManager } from './InputManager';
import { isBuildingUnlocked } from './ResearchSystem';

const ITEM_NAME_BY_TYPE: Record<ItemType, string> = Object.values(ITEMS).reduce(
  (acc, item) => {
    acc[item.type] = item.name;
    return acc;
  },
  {} as Record<ItemType, string>
);

type BuildingAvailabilityInfo = {
  canBuild: boolean;
  canQueue: boolean;
  missingRequirements: BuildingItemRequirement[];
};

// Get buildings by category
export const getBuildingsByCategory = (
  category: BuildingCategory
): Building[] => {
  return Object.values(BUILDINGS).filter(
    building => building.category === category
  );
};

// Check if player can afford building
export const canAfford = (
  building: Building,
  availableItems: Record<ItemType, number>
): boolean => {
  const itemRequirements = building.cost.items ?? [];
  for (const requirement of itemRequirements) {
    const available = availableItems[requirement.item] ?? 0;
    if (available < requirement.amount) {
      return false;
    }
  }

  return true;
};

// Handle building selection with auto-place or build mode
const selectBuilding = (building: Building): boolean => {
  const gameEngine = getGameEngine();

  if (!gameEngine.isBuildingUnlocked(building.id)) {
    return false;
  }

  // Check if we should auto-place instead of entering build mode
  if (gameEngine.isAutoBuildOnSelect()) {
    // Get the target position from the game engine (smart positioning for multi-tile buildings)
    const targetPos = gameEngine.getBuildingTargetLocation(building);

    const success = gameEngine.placeBuilding(
      targetPos.x,
      targetPos.y,
      building
    );
    if (success) {
      // Clear everything after successful placement and dismiss menu completely
      gameEngine.setBuildMenuState({
        buildMenuOpen: false,
        selectedCategory: null,
        selectedBuilding: null,
      });
      return true;
    }
  }

  // Normal building selection - enter build mode
  gameEngine.setBuildMenuState({
    selectedBuilding: building,
    buildMenuOpen: false,
  });
  return true;
};

// BuildMenu props interface
interface BuildMenuProps {
  buildMenuOpen: BuildMenuState;
  selectedCategory: BuildingCategory | null;
  selectedBuilding: Building | null;
}

// Building Status Menu Component
export const BuildMenu: React.FC<BuildMenuProps> = ({
  buildMenuOpen,
  selectedCategory,
  selectedBuilding,
}) => {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const {
    researchState,
    chestInventoryLayer,
    playerInventory,
    handCraftingQueue,
  } = useMapState();
  const postQueueItemCounts = React.useMemo(() => {
    const availableCounts = getAvailableItemCountsForPlacement(
      playerInventory,
      chestInventoryLayer
    );
    return calculateHandCraftingPostQueueInventory(
      availableCounts,
      handCraftingQueue
    );
  }, [playerInventory, chestInventoryLayer, handCraftingQueue]);

  // Focus the menu when it opens
  React.useEffect(() => {
    if (buildMenuOpen !== false && menuRef.current) {
      menuRef.current.focus();
    }
  }, [buildMenuOpen]);

  const gameEngine = getGameEngine();

  const buildingAvailability = React.useMemo(() => {
    const availability = new Map<string, BuildingAvailabilityInfo>();

    Object.values(BUILDINGS).forEach(building => {
      if (!isBuildingUnlocked(researchState, building.id)) {
        availability.set(building.id, {
          canBuild: false,
          canQueue: false,
          missingRequirements: [],
        });
        return;
      }

      const itemRequirements: readonly BuildingItemRequirement[] =
        building.cost.items ?? [];
      const missingRequirements = itemRequirements
        .map((requirement): BuildingItemRequirement | null => {
          const available = postQueueItemCounts[requirement.item] ?? 0;
          const missingAmount = requirement.amount - available;
          if (missingAmount > 0) {
            return {
              item: requirement.item,
              amount: missingAmount,
            };
          }
          return null;
        })
        .filter((req): req is BuildingItemRequirement => req !== null);

      const canBuild = missingRequirements.length === 0;
      const canQueue =
        !canBuild && missingRequirements.length > 0
          ? gameEngine.canQueueHandCraftingForBuilding(
              building,
              missingRequirements
            )
          : false;

      availability.set(building.id, {
        canBuild,
        canQueue,
        missingRequirements,
      });
    });

    return availability;
  }, [gameEngine, postQueueItemCounts, researchState]);

  if (buildMenuOpen === false) return null;

  const handleBuildingClick = (building: Building): void => {
    const availability = buildingAvailability.get(building.id);
    if (!availability) {
      return;
    }

    if (availability.canBuild) {
      selectBuilding(building);
      return;
    }

    if (availability.canQueue) {
      gameEngine.queueHandCraftingForBuilding(
        building,
        availability.missingRequirements
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    // Handle category selection hotkeys
    if (!selectedCategory) {
      if (CONTROLS.CATEGORY_MINING.includes(e.key)) {
        e.preventDefault();
        gameEngine.setBuildMenuState({ selectedCategory: 'mining' });
      } else if (CONTROLS.CATEGORY_PRODUCTION.includes(e.key)) {
        e.preventDefault();
        gameEngine.setBuildMenuState({ selectedCategory: 'production' });
      } else if (CONTROLS.CATEGORY_LOGISTICS.includes(e.key)) {
        e.preventDefault();
        gameEngine.setBuildMenuState({ selectedCategory: 'logistics' });
      } else if (CONTROLS.CATEGORY_POWER.includes(e.key)) {
        e.preventDefault();
        gameEngine.setBuildMenuState({ selectedCategory: 'power' });
      }
    }
    // Handle building selection hotkeys when a category is selected
    else {
      const availableBuildings = getBuildingsByCategory(
        selectedCategory
      ).filter(building => isBuildingUnlocked(researchState, building.id));
      const targetBuilding = availableBuildings.find(
        building => building.hotkey === e.key
      );

      if (targetBuilding) {
        const availability = buildingAvailability.get(targetBuilding.id);
        if (!availability) {
          return;
        }

        if (availability.canBuild) {
          e.preventDefault();
          selectBuilding(targetBuilding);
        } else if (availability.canQueue) {
          e.preventDefault();
          gameEngine.queueHandCraftingForBuilding(
            targetBuilding,
            availability.missingRequirements
          );
        }
      }
    }
  };

  let unlockedBuildings: Building[] = [];
  let categoryHasLockedBuildings = false;

  if (selectedCategory) {
    const buildingsInCategory = getBuildingsByCategory(selectedCategory);
    unlockedBuildings = buildingsInCategory.filter(building =>
      isBuildingUnlocked(researchState, building.id)
    );
    categoryHasLockedBuildings =
      unlockedBuildings.length === 0 && buildingsInCategory.length > 0;
  }

  return (
    <div
      ref={menuRef}
      className='build-menu'
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className='build-menu-header'>
        <h3>Build Menu</h3>
        <p>
          Press <kbd>ESC</kbd> to close, <kbd>B</kbd> to toggle
        </p>
      </div>

      {!selectedCategory ? (
        <div className='category-grid'>
          {Object.entries(BUILDING_CATEGORIES).map(([key, category]) => (
            <div
              key={key}
              className='category-item'
              style={{ borderColor: category.color }}
              onClick={() => {
                // Dispatch InputManager command instead of calling React state setter
                InputManager.getInstance().triggerCommand(
                  createSelectBuildCategoryCommand(key as BuildingCategory)
                );
              }}
            >
              <div className='category-header'>
                <h4 style={{ color: category.color }}>{category.name}</h4>
                <kbd>{category.hotkey.toUpperCase()}</kbd>
              </div>
              <div className='building-preview'>
                {getBuildingsByCategory(key as BuildingCategory)
                  .filter(building =>
                    isBuildingUnlocked(researchState, building.id)
                  )
                  .slice(0, 3)
                  .map(building => (
                    <span key={building.id} className='preview-char'>
                      {building.character}
                    </span>
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='building-list'>
          <div className='category-header'>
            <h4 style={{ color: BUILDING_CATEGORIES[selectedCategory].color }}>
              {BUILDING_CATEGORIES[selectedCategory].name}
            </h4>
            <button
              onClick={() => {
                // Dispatch InputManager command to clear category selection
                InputManager.getInstance().triggerCommand(
                  createSelectBuildCategoryCommand(null)
                );
              }}
            >
              ← Back
            </button>
          </div>

          <div className='building-grid'>
            {unlockedBuildings.map(building => {
              const isSelected =
                selectedBuilding && selectedBuilding.id === building.id;
              const availability = buildingAvailability.get(building.id);
              const canBuildNow = availability?.canBuild ?? false;
              const canQueueCrafting = availability?.canQueue ?? false;
              const canInitiateBuild = canBuildNow || canQueueCrafting;
              const itemRequirements: readonly BuildingItemRequirement[] =
                building.cost.items ?? [];
              const itemCostDetails = itemRequirements.map(
                (
                  requirement
                ): {
                  item: ItemType;
                  amount: number;
                  name: string;
                  available: number;
                } => {
                  const itemName =
                    ITEM_NAME_BY_TYPE[requirement.item] ?? requirement.item;
                  const available = postQueueItemCounts[requirement.item] ?? 0;
                  return {
                    item: requirement.item,
                    amount: requirement.amount,
                    name: itemName,
                    available,
                  };
                }
              );
              const materialCostText = itemCostDetails.length
                ? itemCostDetails
                    .map(detail => {
                      const base = `${detail.amount} ${detail.name}`;
                      return detail.available >= detail.amount
                        ? base
                        : `${base} (have ${detail.available})`;
                    })
                    .join(', ')
                : 'Free';
              const costDescription =
                !canBuildNow && canQueueCrafting && itemCostDetails.length > 0
                  ? `${materialCostText} (will queue crafting)`
                  : materialCostText;
              const availabilityClass = canInitiateBuild
                ? 'affordable'
                : 'expensive';
              const selectedClass = isSelected ? 'selected' : '';
              const className = `building-item ${availabilityClass} ${selectedClass}`;
              const clickHandler = canInitiateBuild
                ? () => handleBuildingClick(building)
                : undefined;

              return (
                <div
                  key={building.id}
                  className={className}
                  onClick={clickHandler}
                  aria-disabled={!canInitiateBuild}
                >
                  <div className='building-icon'>{building.character}</div>
                  <div className='building-info'>
                    <div className='building-header'>
                      <div className='building-name'>{building.name}</div>
                      <kbd>{building.hotkey}</kbd>
                    </div>
                    <div className='building-cost'>Cost: {costDescription}</div>
                    <div className='building-description'>
                      {building.description}
                    </div>
                  </div>
                </div>
              );
            })}
            {categoryHasLockedBuildings && (
              <div className='building-item locked'>
                <div className='building-info'>
                  <div className='building-description'>
                    No buildings unlocked yet. Research technologies to unlock
                    more options.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
